
/* global google */
import Vue, { PropOptions } from 'vue';
import _ from 'lodash';
import axios from 'axios';
import cdatepicker from './CDatepicker.vue';
import { EventsService } from '@/services/EventsService';
import { Table } from '@/models/page-models/page-items/Table';
import { TableHeader } from '@/models/page-models/TableHeader';

export default Vue.extend({
  name: 'CTable',
  components: { cdatepicker },
  props: {
    input: { type: Object, default: () => {} } as PropOptions<Table>,
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} },
    rowDisabled: { type: Boolean, default: false }
  },
  data(): {
    isExporting: boolean,
    tableVariable: Array<any>,
    selected: Array<any>,
    search: string,
    rules: any,
    page: number
  } {
    return {
      isExporting: false,
      tableVariable: [],
      selected: [],
      search: '',
      rules: {
        required(required: boolean) {
          if (required) {
            return (value: any) => !!value || 'Required';
          }
          return true;
        },
      },
      page: 1
    };
  },
  computed: {
    tableItems: function (): any[] {
      if(this.input.tableType == 'static') {
        const items: any[] = this.parent._getVariable(this.input.items) || [];
        return items.map((_item: any, __key: number) =>{
          const itemKey = this.input.selectableUniqueId && (_.get(_item, this.input.selectableUniqueId) || this.parent._resolveFormulaValue(this.input.selectableUniqueId, { _item }))
          return { ..._item, __key, itemKey }
        });
      } else if(this.input.tableType == 'dynamic') {
        const tableItemsSource: any[] = this.parent._getVariable(this.input.items) || [];
        const items: any[] = Array.isArray(this.tableVariable) ? this.tableVariable.map((formDataRow: any, __key: Number) => {
          const foundItem = tableItemsSource.find((sourceItem: any) => {
            if(!this.input.itemKey || !this.input.variableKey) return false;
            return _.get(sourceItem, this.input.itemKey) === _.get(formDataRow, this.input.variableKey);
          });
          const itemKey = this.input.selectableUniqueId && (_.get(foundItem, this.input.selectableUniqueId) || this.parent._resolveFormulaValue(this.input.selectableUniqueId, { _item: foundItem }))
          return {... foundItem, __key, itemKey };
        }) : [];
        return items;
      }
      return [];
    },
    printTableData: function() : {[key: string]: string }[] {
      if(this.input.tableType == 'dynamic') return [];
      const tableHeaders = this.getTableHeaders(this.input)
        .filter((tableHeader: TableHeader) => !tableHeader.type || !(['button', 'actions'].includes(tableHeader.type)));
      let printTableData = this.tableItems.map(item => {
        let formattedItem: any = {};
        tableHeaders.forEach((tableHeader: TableHeader) => {
          formattedItem[tableHeader.text] = this.showMultipleItemsInRow(_.get(item, tableHeader.value), item, tableHeader);
        });
        return formattedItem;
      });
      const resolveHeaderFooterObject = (headerFooterObject: any) => {
        let resolvedObject: any = {};
        if(headerFooterObject) {
          _.forEach(tableHeaders, (tableHeader: TableHeader) => {
            if(headerFooterObject[tableHeader.value]) {
              resolvedObject[tableHeader.text] = this.parent._resolveFormulaValue(headerFooterObject[tableHeader.value], { _item: this.tableItems });
            } else {
              resolvedObject[tableHeader.text] = '';
            }
          });
          return resolvedObject;
        }
        return {};
      }
      if(this.input.belowHeader) {
        const headerObject = resolveHeaderFooterObject(this.input.belowHeader);
        printTableData = [ headerObject, ...printTableData ];
      }
      if(this.input.footer) {
        const footerObject = resolveHeaderFooterObject(this.input.footer);
        printTableData = [ ...printTableData, footerObject ];
      }
      return printTableData;
    },
    exportFileName: function(): string {
      if(this.input.googlSheetFileName) {
        return this.parent._resolveFormulaValue(this.input.googlSheetFileName);
      }
      const todaysDateString = new Date().toDateString();
      return `${this.input.label}_${todaysDateString}`.replace(/ /g, '_');
    },
    defaultPageNo: function(): number {
      return this.input.pagination ? this.getVariable(this.input.pagination.pageVariable): -1;
    },
    serverItemsLength: function(): number {
      return this.input.pagination ? this.getVariable(this.input.pagination.serverItemsLengthVariable) : -1;
    },
    itemsPerPage: function(): number {
      let itemsPerPage = 20;
      if(_.get(this.input, 'pagination.itemsPerPageVariable')) {
        itemsPerPage = this.getVariable(this.input.pagination?.itemsPerPageVariable) || itemsPerPage;
      }
      return itemsPerPage;
    }
  },
  watch: {
    async selected() {
      if(this.selected.length > 0) await this.parent._callMultipleFunctions(this.input.onSelectedChange, this.selected);
      if(this.input.searchKeyOnSelected) {
        this.search = this.selected.length > 0 ? `${_.get(this.selected[0], this.input.searchKeyOnSelected)}` : '';
      }
    },
    tableItems() {
      this.selected = [];
      if(!this.input.searchFunctions) this.search = '';
    },
    defaultPageNo() {
      this.page = this.defaultPageNo;
    }
  },
  async mounted() {
    EventsService.createEvent(`${this.input.validationVariable}Validation`, this.validateForm, this);
    if(this.input.tableType == 'dynamic') {
      this.$watch(
        () => this.parent._getVariable(this.input.variable), 
        async (tableVariable) => {
          if (tableVariable) {
            if(this.input.validationVariable) this.updateValidation(tableVariable);
            // link variable with store
            this.tableVariable = tableVariable;
            await this.parent._callMultipleFunctions(this.input.onSelectedChange, this.selected);
          }
        },
        {
          immediate: true, deep: true
        }
      )
    }
  },
  methods: {
    validateForm: function() {
      // @ts-ignore
      this.$refs.tableForm.validate();
    },
    /**
     * Invoked to resolve formula values in table
     * @param formulaString the string which needs to be resolved 
     * @param item the corresponding item of a row
     * @param header the header variable for that column
     * @param tableVariable (optional) the corresponding tableVariable of a row in case of dynamic tables
     */
    resolveFormulaValue(formulaString: string, item: any, header: any, tableVariable?: any): any {
      let _variable = tableVariable;
      if(this.input.tableType === 'dynamic') {
        _variable = item && !isNaN(item.__key) && tableVariable[item.__key];
      }
      return this.parent._resolveFormulaValue(formulaString.replace('\\n','\n'), { _item: header.itemKey ? item[header.itemKey] : item, _variable }, item.__key);
    },
    showMultipleItemsInRow(value: any, item: any, header: any, tableVariable?: any): any {
      let returnVariable = '';
      const resolveItemValue = (value: any, tableItem: any) =>  {
        if(header.type === 'formula' || header.formula) {
          return this.parent._checkFilter(header, this.resolveFormulaValue(header.formula, tableItem, header, tableVariable), header.filterParam && this.resolveFormulaValue(header.filterParam, tableItem, header, tableVariable))
        } else {
          const filterVariable = header.variableType && header.variableType === 'item' ? item: value;
          return this.parent._checkFilter(header, filterVariable, header.filterParam && this.resolveFormulaValue(header.filterParam, tableItem, header, tableVariable))
        }
      }
      if(header && header.multipleItemsVariable) {
        const multipleItems: any[] = _.get(item, header.multipleItemsVariable);
        multipleItems.forEach((currentItem) => {
          const currentValue = header.itemParam ? _.get(currentItem, header.itemParam) : currentItem;
          const resolvedValue = resolveItemValue(currentValue, currentItem);
          returnVariable = returnVariable + resolvedValue + '\n';
        })
        returnVariable = returnVariable.trim();
      } else {
        returnVariable = resolveItemValue(value, item);
      }
      return returnVariable;
    },
    getMultipleItemsInRow(item: any, header: any, tableVariable?: any): any {
      let returnVariable: any[] = [];
      const resolveItemValue = (value: any, tableItem: any) =>  {
        const filterVariable = header.variableType && header.variableType === 'item' ? item: value;
        return this.parent._checkFilter(header, filterVariable, header.filterParam && this.resolveFormulaValue(header.filterParam, tableItem, header, tableVariable))
      }
      const multipleItems: any[] = _.get(item, header.multipleItemsVariable);
      multipleItems.forEach((currentItem) => {
        const currentValue = header.itemParam ? _.get(currentItem, header.itemParam) : currentItem;
        const resolvedValue = resolveItemValue(currentValue, currentItem);
        returnVariable.push({ resolvedValue, currentItem });
      })
      return returnVariable;
    },
    /**
     * Invoked when the group actions abovee the table are clicked in a table with multiselect option
     * @param onClick the function(s) to be invoked on each item selected. 
     */
    async clickGroupAction(onClick: any[]) {
      const fnsToExecuteOnce = _.filter(onClick, (pageFunctionEvent: any) => _.get(pageFunctionEvent,'executeOnce'));
      const fnsToExecuteEachTime = _.filter(onClick, (pageFunctionEvent: any) => !_.get(pageFunctionEvent,'executeOnce'));
      const promises = this.selected.map(async (item: any) => await this.parent._callMultipleFunctions(fnsToExecuteEachTime, item));
      await Promise.all(promises);
      await this.parent._callMultipleFunctions(fnsToExecuteOnce, this.selected);
    },
    /**
     * Invoked when the a row inside a table is clicked
     * @param item the corresponding item object with respect to the row 
     * @param input the table config
     */
    clickRow(item: any, input: Table) {
      if(input.onRowClick && input.onRowClick.type === 'url') {
        this.$router.push(this.replaceItemValueInObject(input.onRowClick.value, item));
      }
    },
    /**
     * replace `__item.key` in `inputString` with `item.key` 
     * @param inputString the corresponding item object with respect to the row 
     * @param item the corresponding object
     */
    replaceItemValueInObject(inputString: string, item: any): string {
      return inputString.replace(/__item\.([^/]*)/g, (match, p1) => _.get(item, p1));
    },
    /**
     * Used to retrieve headers for table and fiter out actions column if necessary options are not available
     * @param input the table config
     */
    getTableHeaders(input: Table): TableHeader[] {
      const tableConfigHeaders : TableHeader[] = input.headers.map((column: any) => {
        if (column.value.startsWith('__item.')) {
          column.value = column.value.split('__item.')[1];
        }
        column.align = column.align || 'center' + ` ${column.columnClass || ''}`;
        column.text = this.resolveFormulaValue(column.text.replace('\\n','\n'), {}, column)
        return column;
      })
      const filteredTableConfigHeaders = tableConfigHeaders.filter(header => {
        if(header.showIf) {
          return this.resolveFormulaValue(header.showIf, {}, header)
        }
        return true;
      })
      const hasActions = input.canDelete  || input.showActions;
      if(this.rowDisabled || this.parent._getDisableStatus(this.input.disabled) || !hasActions) {
        return filteredTableConfigHeaders.filter((header: TableHeader) => header.value !== 'actions'); 
      }
      return filteredTableConfigHeaders;
    },
    getHeaderAlignment(headerName: string): string {
      const headers = this.getTableHeaders(this.input);
      const selectedHeader = headers.find((header: TableHeader) => header.text == headerName);
      return _.get(selectedHeader, 'align', '');
    },
    onDelete(targetItem: any, confirmation : boolean = false) {
      const index = this.tableVariable.indexOf(targetItem);
      if (!confirmation || (confirmation && confirm('Are you sure you want to delete this item?'))) {
        this.tableVariable.splice(index, 1);
        if(this.input.executeOnDelete) {
          this.parent._callMultipleFunctions(this.input.executeOnDelete);
        }
      }
    },
    getTableActions(header: any): any[] {
      if(header.useMenu) {
        return header.actions.filter((action: any) => action.priority === 'main');
      } else {
        return header.actions;
      }
    },
    getActionMenuItems(item: any, header: any, tableVariable?: any): any[] {
      return header.actions.filter((action: any) => 
        action.priority === 'menu' && (action.showIf && this.resolveFormulaValue(action.showIf, item, header, tableVariable) || !action.showIf)
      );
    },
    updateValidation(tableVariable: any) {
      const headers = this.getTableHeaders(this.input);
      const requiredHeaders = headers.filter(header => header.required).map(header => header.value);
      const validationStatus = _.every(tableVariable, (rowVariable) => {
        return _.every(requiredHeaders, (header) => _.get(rowVariable, header))
      })
      if(this.input.validationVariable) {
        this.$store.commit('setFormValidStatus', { [this.input.validationVariable]: validationStatus });
      }
    },
    changePage(newPage: any) {
      this.page = newPage;
      this.updatePageVariable();
      this.parent._callMultipleFunctions(this.input.pagination?.pageChangeFns);
    },
    updatePageVariable() {
      const { prefix, key } = this.parent._getPrefixAndKey(this.input.pagination?.pageVariable);
      this.$set(this.getVariable(prefix), key, this.page );
    },
    getItemClass(itemVariable: any): string {
      return this.parent._resolveFormulaValue(this.input.rowClassFormula, { _item: itemVariable });
    },
    callSearchFn() {
      if(this.input.searchFunctions) {
        this.parent._callMultipleFunctions(this.input.searchFunctions, this.search);
      }
    },
    prepareSheetData() {
      if(this.input.tableType == 'dynamic') return [];
      const tableHeaders = this.getTableHeaders(this.input)
        .filter((tableHeader: TableHeader) => !tableHeader.type || !(['button', 'actions'].includes(tableHeader.type)));
      let headerRow = tableHeaders.map(header => header.text);
      let dataRows = this.printTableData.map(tableRow => Object.values(tableRow));
      return [headerRow, ...dataRows];
    },
    exportToGoogleSheet() {
      this.isExporting = true;

      // Initialize the Google Token Client
      // @ts-ignore
      const client = google.accounts.oauth2.initTokenClient({
        client_id: process.env.VUE_APP_GOOGLE_SHEET_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: async (tokenResponse: any) => {
          if (tokenResponse.error !== undefined) {
            console.error('Auth error:', tokenResponse.error);
            this.isExporting = false;
            return;
          }
          
          // Token received, proceed to create the sheet
          await this.createAndPopulateSheet(tokenResponse.access_token);
        },
        // Handles Window-level errors (e.g., user closed the popup)
        error_callback: (error: any) => {
          if (error.type === 'popup_closed') {
            console.warn('Authentication popup was closed by the user.');
            this.$store.commit('setSnackbar', { serverMessage: 'The Google sign-in window was closed. Please try again and complete the required security steps.', show: true, color: 'error' });
          } else if (error.type === 'popup_failed_to_open') {
            console.error('Popup blocked by the browser.');
            this.$store.commit('setSnackbar', { serverMessage: 'Your browser blocked the sign-in popup. Please allow popups for this site.', show: true, color: 'error' });
          } else {
            console.error('Unknown Google Auth error:', error);
          }
          this.isExporting = false;
        }
      });

      // Request the access token
      client.requestAccessToken();
    },
    async createAndPopulateSheet(accessToken: string) {
      try {
        const sheetData = this.prepareSheetData();

        const axiosConfig = {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        };

        // A: Create a new empty spreadsheet
        const createRes = await axios.post(
          'https://sheets.googleapis.com/v4/spreadsheets',
          {
            properties: { title: this.exportFileName }
          },
          axiosConfig
        );
        
        const spreadsheetId = createRes.data.spreadsheetId;
        const sheetId = createRes.data.sheets[0].properties.sheetId;

        // B: Insert the data
        await axios.post(
          `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Sheet1!A1:append?valueInputOption=USER_ENTERED`,
          {
            values: sheetData
          },
          axiosConfig
        );

        // C: Format the Sheet (Freeze and Bold Header)
        await axios.post(
          `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
          {
            requests: [
              {
                updateSheetProperties: {
                  properties: {
                    sheetId: sheetId,
                    gridProperties: { frozenRowCount: 1 }
                  },
                  fields: 'gridProperties.frozenRowCount'
                }
              },
              {
                repeatCell: {
                  range: {
                    sheetId: sheetId,
                    startRowIndex: 0,
                    endRowIndex: 1
                  },
                  cell: {
                    userEnteredFormat: {
                      textFormat: { bold: true }
                    }
                  },
                  fields: 'userEnteredFormat.textFormat.bold'
                }
              }
            ]
          },
          axiosConfig
        );

        this.$store.commit('setSnackbar', { serverMessage: 'Export successful! Check your Google Drive.', show: true, color: 'secondary' });
        window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`, '_blank');
      } catch (error) {
        console.error('Error exporting to Sheets:', error);
        this.$store.commit('setSnackbar', { serverMessage: 'Error exporting to Sheets', show: true, color: 'error' });
      } finally {
        this.isExporting = false;
      }
    }
  }
});
